import React from 'react';
import { LayoutGrid, List as ListIcon } from 'lucide-react';

/**
 * ViewModeToggle — shared Cards/List switch used across list pages
 * (Field Reports, Approved Reports, Review Queue, Maintenance, Equipment).
 *
 * Persists per-page via localStorage when `storageKey` is provided.
 *
 * Usage:
 *   const [viewMode, setViewMode] = useViewMode('approvedReports.viewMode');
 *   <ViewModeToggle value={viewMode} onChange={setViewMode} />
 */
export function ViewModeToggle({ value, onChange, className = '' }) {
  return (
    <div
      className={`flex items-center h-9 border border-slate-200 rounded-md overflow-hidden bg-white ${className}`}
      data-testid="view-mode-toggle"
    >
      <button
        type="button"
        onClick={() => onChange('cards')}
        className={`px-3 h-full text-sm flex items-center gap-1 transition-colors ${
          value === 'cards' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'
        }`}
        data-testid="view-mode-cards"
        aria-pressed={value === 'cards'}
      >
        <LayoutGrid className="w-4 h-4" />
        Cards
      </button>
      <button
        type="button"
        onClick={() => onChange('list')}
        className={`px-3 h-full text-sm flex items-center gap-1 border-l border-slate-200 transition-colors ${
          value === 'list' ? 'bg-slate-100 text-slate-900 font-semibold' : 'text-slate-500 hover:bg-slate-50'
        }`}
        data-testid="view-mode-list"
        aria-pressed={value === 'list'}
      >
        <ListIcon className="w-4 h-4" />
        List
      </button>
    </div>
  );
}

/**
 * useViewMode — hook that backs ViewModeToggle with localStorage persistence.
 */
export function useViewMode(storageKey, defaultMode = 'cards') {
  const [mode, setModeState] = React.useState(() => {
    if (!storageKey) return defaultMode;
    try {
      const saved = localStorage.getItem(storageKey);
      return saved === 'cards' || saved === 'list' ? saved : defaultMode;
    } catch {
      return defaultMode;
    }
  });
  const setMode = React.useCallback(
    (next) => {
      setModeState(next);
      if (storageKey) {
        try { localStorage.setItem(storageKey, next); } catch {}
      }
    },
    [storageKey]
  );
  return [mode, setMode];
}

export default ViewModeToggle;
